<template>
  <v-container class="d-flex flex-column pa-0 h-100" style="gap: 1.5rem" fluid>
    <v-row
      v-if="visibleRejectedBills.length > 0"
      no-gutters
      class="d-flex flex-column flex-grow-0"
      style="gap: 1rem"
    >
      <v-col v-for="bill of visibleRejectedBills" :key="bill.id">
        <BillErrorAlert :bill="bill" @hidden="onBillAlertHidden" />
      </v-col>
    </v-row>

    <v-row v-if="showBillAlert" no-gutters class="flex-grow-0">
      <v-col>
        <BillAlert show-close-button />
      </v-col>
    </v-row>

    <v-row no-gutters class="flex-grow-0" style="gap: 1rem">
      <v-col cols="12" md="8">
        <v-text-field
          autofocus
          outlined
          dense
          clearable
          hide-details
          :value="pagination.search"
          @input="debounceSearch($event)"
          @click:clear="clearSearch()"
          flat
          prepend-inner-icon="mdi-magnify"
          label="Buscar"
          color="primary"
        />
      </v-col>

      <v-col>
        <v-select
          dense
          label="Estado"
          outlined
          :value="pagination.status"
          @input="debounceStatus($event)"
          :items="statusOptions"
          item-text="name"
          item-value="value"
          hide-details
          clearable
          flat
          multiple
        />
      </v-col>
    </v-row>

    <v-row no-gutters v-if="requestLoading" class="loader-container">
      <Loader text="Cargando pedidos" />
    </v-row>

    <v-row
      class="h-100 flex-grow-1"
      no-gutters
      v-else-if="!requestLoading && requests?.length > 0"
    >
      <v-col v-if="$store.state.headers" cols="12" class="hidden-sm-and-down">
        <v-data-table
          :headers="computedHeaders"
          :items="requests"
          :loading="requestLoading"
          :page.sync="pagination.page"
          :items-per-page.sync="pagination.itemsPerPage"
          :server-items-length="totalRequests"
          :footer-props="{
            itemsPerPageOptions: pagination.rowsPerPageOption,
            itemsPerPageText: 'Pedidos por página',
          }"
          style="width: 100%"
          loading-text="Cargando listado..."
          no-data-text="No se encontraron pedidos"
          hide-default-header
        >
          <template v-slot:header>
            <tr>
              <th v-show="showColumn('request.display_id')">ID</th>
              <th v-show="showColumn('request.status')">ESTADO</th>
              <th v-show="showColumn('request.rubro')">RUBRO</th>
              <th v-show="showColumn('request.service')">SERVICIO</th>
              <th v-show="showClientColumn('request.client')">CLIENTE</th>
              <th v-show="showColumn('request.address')">DIRECCION</th>
              <th v-show="showColumn('request.city')">CIUDAD</th>
            </tr>
          </template>
          <template v-slot:item="{ item: request }">
            <tr v-if="request.is_urgency" class="urgent header">
              <td :colspan="7" style="height: 30px">
                <b style="color: #ff5252"
                  ><span style="font-size: 12px">&#x21E9;</span> PEDIDO URGENTE
                  <span style="font-size: 12px">&#x21E9;</span></b
                >
              </td>
            </tr>
            <tr
              @click="showRequest(request)"
              :style="{ cursor: isNavigating ? 'progress' : 'pointer' }"
              :class="{
                urgent: request.is_urgency,
                'disabled-item': isNavigating,
              }"
            >
              <td v-show="showColumn('request.display_id')">
                <div style="margin: 0" class="id-column-event">
                  <span>{{ request.display_id ?? request.id }}</span>
                  <v-tooltip bottom>
                    <template
                      v-slot:activator="{ on, attrs }"
                      v-if="$store.state.app_display_scheme && request.scheme"
                    >
                      <div v-bind="attrs" v-on="on" class="mr-2">
                        <SchemeIcon
                          :scheme="request.scheme"
                          class="icon-size"
                        />
                      </div>
                    </template>
                    <span>{{ request.scheme }}</span>
                  </v-tooltip>
                </div>
              </td>
              <td v-show="showColumn('request.status')">
                {{ request.status ?? "No disponible" }}
              </td>
              <td v-show="showColumn('request.rubro')">
                {{ request.rubro ?? "No disponible" }}
              </td>
              <td v-show="showColumn('request.service')">
                {{ request.service ?? "No disponible" }}
              </td>
              <td v-show="showClientColumn('request.client')">
                {{ request.client ?? "No disponible" }}
              </td>
              <td v-show="showColumn('request.address')">
                {{ request.address ?? "No disponible" }}
              </td>
              <td v-show="showColumn('request.city')">
                {{ request.city ?? "No disponible" }}
              </td>
            </tr>
          </template>
        </v-data-table>
      </v-col>

      <!-- Mobile -->
      <v-col cols="12" class="hidden-md-and-up">
        <v-data-table
          :items="requests"
          :loading="requestLoading"
          :headers="computedHeaders"
          :page.sync="pagination.page"
          :items-per-page.sync="pagination.itemsPerPage"
          :server-items-length="totalRequests"
          :footer-props="{
            itemsPerPageOptions: pagination.rowsPerPageOption,
            itemsPerPageText: 'Pedidos por página',
          }"
          style="width: 100%"
          hide-default-header
          loading-text="Cargando listado..."
          no-data-text="No se encontraron pedidos"
        >
          <template v-slot:item="{ item }">
            <RequestItem
              :request="item"
              :is-navigating="isNavigating"
              @show-request="showRequest(item)"
            />
          </template>
        </v-data-table>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { mapActions, mapGetters, mapState } from "vuex";
import debounce from "lodash/debounce";

import RequestItem from "./RequestItem.vue";
import HeadersRequests from "../assets/js/headersRequests";
import BillAlert from "./BillAlert.vue";
import SchemeIcon from "./SchemeIcon.vue";
import Loader from "./Loader.vue";
import BillErrorAlert from "./BillErrorAlert.vue";

export default {
  name: "request-list",
  components: {
    RequestItem,
    SchemeIcon,
    BillAlert,
    Loader,
    BillErrorAlert,
  },
  data() {
    return {
      headers: null,
      headersRequests: HeadersRequests,
      pagination: {
        page: 1,
        itemsPerPage: 10,
        rowsPerPageOption: [5, 10, 20, 30],
        search: null,
        status: null,
      },
      isNavigating: false,
      alertVisibilityTick: 0,
    };
  },
  created() {
    this.getAllRequests({ pagination: this.pagination });
    this.getBills();
  },
  mounted() {
    this.$store.commit("set_app_title", "Pedidos");
    window.scrollTo(0, 0);
    this.setHeaders();
  },
  methods: {
    ...mapActions("request", ["getAllRequests"]),
    ...mapActions("invoice", ["getBills"]),

    onBillAlertHidden() {
      this.alertVisibilityTick += 1;
    },

    listRequests: debounce(async function ({ pagination }) {
      try {
        await this.getAllRequests({ pagination });
      } catch (error) {
        console.log(error);
      }
    }, 500),

    debounceSearch: debounce(function (event) {
      this.pagination.search = event;
    }, 500),

    debounceStatus: debounce(function (event) {
      console.log(event);
      this.pagination.status = event;
    }, 500),

    resetPaginationPage() {
      this.pagination.page = 1;
    },
    clearSearch() {
      this.pagination.search = null;
    },
    setHeaders() {
      const _headers = localStorage.getItem("headersRequests");
      let headers;
      if (_headers) {
        headers = JSON.parse(_headers);
      } else {
        headers = this.headersRequests;
        localStorage.setItem("headersRequests", JSON.stringify(headers));
      }
      // this.injectFilters(headers);
      this.$store.commit("set_headers", headers);
    },
    // injectFilters(headers) {
    //   const filter = (value) => {
    //     if (!this.$store.state.request_status) return true;
    //     return value === this.$store.state.request_status;
    //   };
    //   const index = headers.findIndex(
    //     (item) => value === "request.status_request.id"
    //   );
    //   headers[index].filter = filter;
    //   const indexB = this.headersRequests.findIndex(
    //     (item) => value === "request.status_request.id"
    //   );
    //   this.headersRequests[indexB].filter = filter;
    // },
    async showRequest(request) {
      if (this.isNavigating) return;

      this.isNavigating = true;

      const navigationResult = await this.$router.push({
        name: "request-detail",
        params: { id: request.id },
      });

      if (navigationResult) {
        this.isNavigating = false;
      }
    },
    showColumn(value) {
      const item = this.$store.state.headers.find(function (element) {
        return element.value === value;
      });
      if (item) return item.show;
      return null;
    },
    showClientColumn(value) {
      const item = this.$store.state.headers.find(function (element) {
        return element.value === value;
      });
      if (item && this.$store.state.app_display_username) return item.show;
      return null;
    },
  },
  computed: {
    ...mapState("request", ["requests", "totalRequests", "statusOptions"]),
    ...mapState("invoice", {
      bills: (state) => state.invoices,
      amountToInvoice: (state) => state.totalAmountToInvoice,
      showAlert: (state) => state.showAlert,
    }),
    ...mapGetters("request", ["requestLoading"]),

    showBillAlert() {
      return this.showAlert && this.amountToInvoice > 0;
    },

    computedHeaders: function () {
      let _headers = this.headersRequests;
      if (!this.$store.state.app_display_username) {
        _headers = _headers.filter((value) => value !== "request.client");
      }
      return _headers;
    },
    rejectedBills() {
      return (this.bills || []).filter((bill) =>
        (bill.transacciones || []).some(
          (t) => Number(t.status) === 5 && (t.show_rejected_notify ?? false)
        )
      );
    },
    visibleRejectedBills() {
      // Depend on tick so the list updates when an alert is dismissed
      void this.alertVisibilityTick;

      return this.rejectedBills.filter((bill) => {
        const storedValue = localStorage.getItem(
          `hidden-alert-bill-${bill.id}`
        );
        return storedValue !== "hidden";
      });
    },
  },
  watch: {
    pagination: {
      handler: function (newVal) {
        if (newVal) {
          this.listRequests({ pagination: newVal });
        }
      },
      deep: true,
    },
    "pagination.search": "resetPaginationPage",
    "pagination.status": "resetPaginationPage",
    "$store.state.headers": function (val, old) {
      localStorage.setItem(
        "headersRequests",
        JSON.stringify(this.$store.state.headers)
      );
    },
    "$store.state.updateHeaders": function (val, old) {
      this.setHeaders();
    },
  },
};
</script>
<style scoped>
.loader-container {
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}
.icon-size {
  max-width: 25px;
  max-height: 20px;
  margin-top: 3px;
}
.disabled-item {
  opacity: 0.7;
}

@media (max-width: 959px) {
  .gap-row {
    gap: 0rem;
  }
}

@media (min-width: 960px) {
  .gap-row {
    gap: 1rem;
  }
}
</style>
<style>
.id-icon-event {
  width: 25px;
}
.id-column-event {
  display: flex;
  flex-wrap: nowrap;
  flex-direction: row-reverse;
  justify-content: flex-end;
  align-items: center;
}
.urgent {
  background-color: #fffbcb;
}
.header:hover {
  background-color: #fffbcb !important;
}
@media (max-width: 959px) {
  .v-divider {
    margin-top: 10px;
    margin-bottom: 10px;
  }
  .hidden-sm {
    display: none;
  }
  .section-container {
    padding: 0;
  }
}
@media (min-width: 960px) {
  .v-divider {
    margin-top: 15px;
    margin-bottom: 15px;
  }
  .hidden-lg {
    display: none;
  }
}
.v-list-item__subtitle,
.v-list-item__title {
  overflow: visible !important;
}
.v-data-footer {
  padding: 0 !important;
}
.v-application--is-ltr .v-data-footer__select {
  margin-right: 0 !important;
}
</style>
